import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase-client';

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatImpact(impact = {}) {
  const direction = String(impact?.direction || '').toLowerCase();
  const days = Math.max(0, Number(impact?.days || 0));
  if (direction === 'shortened') return `${days} day${days === 1 ? '' : 's'} earlier`;
  if (direction === 'extended') return `${days} day${days === 1 ? '' : 's'} later`;
  return 'No date change';
}

export default function MembershipYearReanchorModal({
  isOpen,
  onClose,
  teamId,
  onApplied,
  brand = {},
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    setApplied(null);
    try {
      const previewFn = httpsCallable(functions, 'previewMembershipYearReanchor');
      const result = await previewFn({ teamId });
      setPreview(result?.data || null);
    } catch (err) {
      setPreview(null);
      setError(err?.message || 'Unable to preview membership-year alignment.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    } else {
      setPreview(null);
      setError('');
      setApplied(null);
      setApplying(false);
    }
  }, [isOpen, loadPreview]);

  const counts = useMemo(() => preview?.counts || {}, [preview]);
  const hasEligible = Number(counts.totalEligible || 0) > 0;
  const summary = useMemo(() => {
    if (!preview) return [];
    return [
      ['Manual members', counts.manual || 0],
      ['Stripe yearly subscriptions', counts.subscription || 0],
      ['Moved earlier', counts.shortened || 0],
      ['Moved later', counts.extended || 0],
      ['Already aligned', counts.alreadyAligned || 0],
      ['Manual review', counts.manualReview || 0],
      ['Skipped monthly/inactive', counts.skipped || 0],
      ['Failed preview', counts.failed || 0],
    ];
  }, [preview, counts]);

  const handleApply = async () => {
    if (!teamId || !preview?.runId || applying) return;
    setApplying(true);
    setError('');
    try {
      const applyFn = httpsCallable(functions, 'applyMembershipYearReanchor');
      const result = await applyFn({ teamId, runId: preview.runId });
      setApplied(result?.data || null);
      await onApplied?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Unable to apply membership-year alignment.');
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;
  const modalStyle = {
    ...(brand?.primaryColor && { '--team-primary': brand.primaryColor }),
    ...(brand?.secondaryColor && { '--team-secondary': brand.secondaryColor }),
    ...(brand?.headingTextColor && { '--team-heading-text': brand.headingTextColor }),
    ...(brand?.bodyTextColor && { '--team-body-text': brand.bodyTextColor }),
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <h2 className="modal-title">Membership Year Updated</h2>
        <div className="modal-form">
          {loading ? (
            <p className="text-muted">Preparing preview...</p>
          ) : error ? (
            <div className="form-error">{error}</div>
          ) : preview ? (
            <>
              <p className="text-muted">
                Apply the new membership year to existing yearly members. Their next renewal will align to {formatDate(preview.targetDate)}.
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
                gap: '0.5rem',
              }}>
                {summary.map(([label, value]) => (
                  <div key={label} style={{
                    border: '1px solid color-mix(in srgb, var(--team-primary, #2563eb) 18%, #e5e7eb)',
                    borderTop: '3px solid var(--team-primary, #2563eb)',
                    borderRadius: 8,
                    padding: '0.65rem',
                  }}>
                    <div style={{ fontWeight: 700, color: 'var(--team-primary, #2563eb)' }}>{value}</div>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{label}</div>
                  </div>
                ))}
              </div>

              {Array.isArray(preview.samples) && preview.samples.length > 0 && (
                <div className="form-group">
                  <label>Preview</label>
                  <div style={{ display: 'grid', gap: '0.45rem' }}>
                    {preview.samples.map((sample, index) => (
                      <div key={`${sample.label}-${index}`} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '0.65rem',
                      }}>
                        <strong>{sample.label}</strong>
                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                          {sample.paymentMode === 'subscription' ? 'Stripe subscription' : 'Manual payment'}:
                          {' '}from {formatDate(sample.previousDate)} to {formatDate(sample.targetDate)}
                          {' '}({formatImpact(sample.impact)})
                        </div>
                      </div>
                    ))}
                  </div>
                  {preview.hasMoreSamples && (
                    <p className="text-muted">Additional affected members are included in the counts above.</p>
                  )}
                </div>
              )}

              {applied && (
                <div className="success-message" role="status">
                  Applied to {Number(applied?.counts?.applied || 0)} member{Number(applied?.counts?.applied || 0) === 1 ? '' : 's'}.
                </div>
              )}
            </>
          ) : (
            <p className="text-muted">No preview available.</p>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={applying}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={loadPreview}
              disabled={loading || applying}
            >
              Refresh Preview
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleApply}
              disabled={!hasEligible || loading || applying || Boolean(applied)}
            >
              {applying ? 'Applying...' : 'Apply Membership Year'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
